import { useAllProductsContext } from '@app/components/products/context/AllProductsContext';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import {
  getBuyerIdFromCurrentOrder,
  getDeliveryWindowsByBuyerId,
  getIsHideSwitchForSingleStockTypeEnabled,
  getIsStockTypeSeparationEnabled,
  setExpandedProductFilters,
} from '@app/ducks';
import { getDeliveryWindowsIdsByStockType } from '@app/logic/deliveryWindows';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import { omit } from 'ramda';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FiltersPanel } from './FiltersPanel';
import { FiltersPanelMobile } from './FiltersPanelMobile';

interface Props {
  isDrawerOpen: boolean;
  onDrawerClose: () => void;
  productsDetails?: Record<string, Product.Full>;
}

export const ProductsFiltersPanel = ({ isDrawerOpen, onDrawerClose, productsDetails }: Props) => {
  const dispatch = useDispatch();
  const { totalItems, productAttributes, isLoadingFilters, clearFilters, filters, changeFilters, expandedFilters, allProductsIds } =
    useAllProductsContext();
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);

  const hasStockSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const isHideForSingleStockEnabled = useSelector(getIsHideSwitchForSingleStockTypeEnabled);
  const buyerId = useSelector(getBuyerIdFromCurrentOrder);
  const buyerDeliveryWindows = useSelector(getDeliveryWindowsByBuyerId(buyerId));

  const deliveryWindowIdsByStockType = getDeliveryWindowsIdsByStockType(buyerDeliveryWindows);

  const hasTwoStockTypes = React.useMemo(
    () => !isEmpty(deliveryWindowIdsByStockType.preorder) && !isEmpty(deliveryWindowIdsByStockType.stock),
    [deliveryWindowIdsByStockType.preorder, deliveryWindowIdsByStockType.stock],
  );

  const isStockSwitcherVisible = isDefined(buyerId) && hasStockSeparationEnabled && !(isHideForSingleStockEnabled && !hasTwoStockTypes);

  const handleToggleFilter = (fieldName: string, isExpanded: boolean) => {
    const newExpandedFilters = { ...expandedFilters, [fieldName]: !isExpanded };

    dispatch(setExpandedProductFilters(newExpandedFilters));
  };

  if (isMobile) {
    return (
      <FiltersPanelMobile
        isDrawerOpen={isDrawerOpen}
        onDrawerClose={onDrawerClose}
        filters={filters}
        attributes={productAttributes}
        onFilterChange={changeFilters}
        isLoading={isLoadingFilters || !isDefined(buyerId)}
        hasActiveFilters={!isEmpty(omit(['stockType'], filters))}
        itemsCount={totalItems}
        showAddAll
        allProductsIds={allProductsIds}
        showBarcodeScanner
        showStockSwitcher={isStockSwitcherVisible}
        handleToggleFilter={handleToggleFilter}
        expandedFilters={expandedFilters}
        onClearFilters={clearFilters}
        productsDetails={productsDetails}
      />
    );
  }

  return (
    <FiltersPanel
      onClearFilters={clearFilters}
      hasActiveFilters={!isEmpty(omit(['stockType'], filters))}
      isLoading={isLoadingFilters || !isDefined(buyerId)}
      itemsCount={totalItems}
      showAddAll
      onFilterChange={changeFilters}
      handleToggleFilter={handleToggleFilter}
      showStockSwitcher={isStockSwitcherVisible}
      filters={filters}
      attributes={productAttributes}
      onDrawerClose={onDrawerClose}
      expandedFilters={expandedFilters}
      allProductsIds={allProductsIds}
      productsDetails={productsDetails}
      showBarcodeScanner
      hasBreadcrumbs
    />
  );
};
